import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OfflineModeService } from '../services/offline/offline-mode.service';

const PULL_TEST_OFFLINE_ALLOWED_PATHS = [
  '/pull-testing',
  '/pull-testing/install',
  '/pull-testing/test',
] as const;

/**
 * Strips query, hash, and auxiliary router outlets (e.g. install sidenav) before matching.
 */
function normalizePullTestOfflinePath(url: string): string {
  let path = (url.split('?')[0] || url).split('#')[0];
  const sidenavOutletIdx = path.indexOf('(sidenav:');
  if (sidenavOutletIdx >= 0) {
    path = path.slice(0, sidenavOutletIdx);
  }
  return path;
}

/** Routes allowed while pull-test offline mode is active for the given project. */
export function pullTestOfflineAllowedUrl(projectId: string, url: string): boolean {
  const path = normalizePullTestOfflinePath(url);
  const base = `/project/${projectId}`;
  if (!path.startsWith(base)) {
    return false;
  }
  const rest = path.slice(base.length);
  return (PULL_TEST_OFFLINE_ALLOWED_PATHS as readonly string[]).includes(rest);
}

/** When offline mode is active, only allow pull-test dashboard / install / test routes. */
export const offlineModeBlockGuard: CanActivateFn = async (_route, state) => {
  const offlineMode = inject(OfflineModeService);
  const router = inject(Router);

  await offlineMode.ensureRehydrated();

  if (!offlineMode.isActive()) {
    return true;
  }

  const session = offlineMode.getActiveSession();
  if (!session) {
    return true;
  }

  const url = state.url.split('?')[0];
  if (pullTestOfflineAllowedUrl(session.projectId, url)) {
    return true;
  }

  return router.parseUrl(`/project/${session.projectId}/pull-testing`);
};
